import {
  InputLayoutField,
  FieldDelimiter,
  OutputLayoutField,
  OutputLayoutFieldWithDetails,
  InputLabelsMap,
  AttributesFromFullAttribute,
  OutputLayoutFieldLookups,
} from '../../fields';
import { OutputLocation } from '../../output-location';
import { ApiResponse, EmptyResponse } from '../../../for-api';
import {
  AttributeLookup,
  BundleAttributesLookup,
  BundleLookup,
  DemographicLookup,
  DeprecatedAttribute,
  DeprecatedLookup,
  FullAttributeLookup,
  ScoreLookup,
  TrendviewLookup,
  AddonProductLookup,
} from '../../lookups';
import { InputLayoutModel, ProgramMetadataModel } from '../common';
import { FullAttribute } from '../../services';

export interface OrderDetails {
  name: string;
  tmsNumber: string;
  contact?: Contact;
  listConvertErrorPctMax?: string;
  doNotProcessDupsFlag?: string;
  noPinPctMax?: string;
}

export interface Contact {
  name: string;
  clientName: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zipCode?: string;
  phoneNumber?: string;
}

export interface ScoresAndAddonProductsInformation {
  scores?: string[];
  trendviewFields?: string[];
  addonProducts?: string[];
}

export interface DemographicsInformation {
  // isDobSelected?: boolean;
  // dobFormat?: string;
  shouldAppendDOBCCYYMMDD?: boolean;
  shouldAppendDOBMMDDCCYY?: boolean;
  isEmploymentDataSelected?: boolean;
  isPhoneNumberDataSelected?: boolean;
  isExperianSocialSelected?: boolean;
  isMlaInSelected?: boolean;
}

export type FieldsInformation = InlineFieldsInformation;

export interface InlineFieldsInformation {
  source: 'Inline';
  fields: InputLayoutField[];
}

export interface InputFileInformation {
  clientFileName: string;
  fileName: string;
  numberOfRecordsExpected: number;
  fieldsInfo: FieldsInformation;
  delimiter: FieldDelimiter;
  hasHeaders?: boolean;
  isRemoteUploadedFile?: boolean;
  detectedHeaders?: Array<Array<string>>;
}

export interface AttributeLike {
  code: string;
  category: string;
  group: string;
}

export interface AttributesPropertyLike {
  attributes: AttributeLike[];
}
export interface ReportsAttributeLike {
  distribution: AttributesPropertyLike;
}
export interface AttributesAndBundlesInformation {
  attributes?: AttributeLike[];
  bundles?: string[];
  fullAttributes?: FullAttribute[];
}

export type OutputLocationsInformation = OutputLocation[];

export interface OutputLayoutInformation {
  delimiter: FieldDelimiter;
  fields: OutputLayoutField[];
  isCustomized?: boolean;
}

export enum ProgramConfigCategory {
  Scores = 'Scores',
  Bundles = 'Bundles',
  Attributes = 'Attributes',
  FullAttributes = 'FullAttributes',
  Reports = 'Reports',
  Demographics = 'Demo',
  Trendview = 'Trendview',
  OutputRequired = 'OutputRequired',
  ClientInput = 'ClientInput',
  Custom = 'Custom',
}

export interface ProgramConfigField {
  category: ProgramConfigCategory;
  configCode: string;
}

export enum FieldType {
  Selected = 'Selected',
  Required = 'OutputRequired',
  Optional = 'Optional',
}

export type ConfigFieldToOutputFieldMapping = OutputLayoutField &
  OutputLayoutFieldWithDetails & {
    category: string;
    configCode: string;
    fieldGroup: string;
    displayName?: string;
    attrGroup?: string;
    attrCategory?: string;
  };

export type ConfigFieldToOutputFieldMappingsApiResponse = ApiResponse<
  ConfigFieldToOutputFieldMapping[]
>;

export interface DeprecatedAscendQuestLookups {
  scoresLookups: DeprecatedLookup[];
  trendviewLookups: DeprecatedLookup[];
  addonProductsLookups: DeprecatedLookup[];
  attributesLookups: DeprecatedAttribute[];
  bundlesLookups: DeprecatedLookup[];
  demographicsLookups: DeprecatedLookup[];
}

export interface AvailableAscendQuestLookups {
  scoresLookups: ScoreLookup[];
  trendviewLookups: TrendviewLookup[];
  addonProductsLookups: AddonProductLookup[];
  attributesLookups: AttributeLookup[];
  bundlesLookups: BundleLookup[];
  fullAttributesLookups: FullAttributeLookup[];
  demographicsLookups: DemographicLookup[];
  inputLabelsMap: InputLabelsMap;
  bundleAttributes: BundleAttributesLookup[];
  attributesFromFullAttribute: AttributesFromFullAttribute[];
  outputLayoutLookups?: OutputLayoutFieldLookups[];
}

export type AvailableAscendQuestLookupsApiResponse =
  ApiResponse<AvailableAscendQuestLookups>;

export type DeprecatedAscendQuestLookupsApiResponse =
  ApiResponse<DeprecatedAscendQuestLookups>;

export type AscendQuestProgramLookups = {
  available: AvailableAscendQuestLookups;
  deprecated: DeprecatedAscendQuestLookups;
};

// Quest 2.0
export type AscendQuestProductDetails = {
  productName: string;
  programName: string;
  clientName: string;
  clientUserEmail: string;
  submitterName: string;
  submitterEmail: string;
  threshold: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config: Record<string, any>;
  inputLayout: Array<InputLayoutModel>;
  outputLayout: Array<InputLayoutModel>;
  programMetaId: string;
};
export type AscendQuestProductInput = {
  productDetails: AscendQuestProductDetails;
};

export type AscendQuestProgram = {
  metaData: ProgramMetadataModel;
  packagesInput: EmptyResponse;
  productInput: AscendQuestProductInput;
};
